import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from "react";
import { useForm, type FieldValues, type UseFormRegister } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildFieldValuesSchema, type SubmitLeadPayload, type FieldDef } from "../../../lib/schemas/index.ts";
import { messageForCode } from "../../../lib/errors.ts";
import type { EventConfig } from "../types.ts";
import { useSubmitLead } from "../api/useSubmitLead.ts";
import { useEntryCode } from "../hooks/useEntryCode.ts";
import { Turnstile } from "./Turnstile.tsx";
import {
  IconCar,
  IconCheck,
  IconLock,
  IconMail,
  IconPerson,
  IconPhone,
  IconShieldCheck,
  IconTrophy,
  IconWarning,
} from "./eventIcons.tsx";

// Celebratory success animation is lazy-loaded so lottie-web ships only to users who
// actually complete the form (keeps it out of the initial bundle for everyone else).
const SuccessAnimation = lazy(() => import("./SuccessAnimation.tsx"));

interface Props {
  event: EventConfig;
  source: "phone" | "kiosk";
  /** When the winner is announced — shown in the success message. */
  winnerAnnounced?: string;
  onSuccess?: () => void;
}

type IconCmp = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

// Leading icon per known field key — text/email/tel fields render it inside the input.
const FIELD_ICONS: Record<string, IconCmp> = {
  full_name: IconPerson,
  name: IconPerson,
  email: IconMail,
  phone: IconPhone,
  mobile: IconPhone,
  vehicle: IconCar,
};

const LABEL_CLS = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white/55";
const INPUT_CLS =
  "h-12 w-full rounded-xl border border-white/15 bg-white/[0.03] text-[15px] text-white placeholder:text-white/35 " +
  "transition-colors hover:border-flame/30 focus:border-flame/50 focus:outline-none focus:ring-2 focus:ring-flame/40";

const MAILTO_DELETE = `mailto:contact@restomag.com?subject=${encodeURIComponent("Delete my entry")}`;

export function LeadForm({ event, source, winnerAnnounced, onSuccess }: Props) {
  const schema = useMemo(() => buildFieldValuesSchema(event.fieldSet), [event.fieldSet]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FieldValues>({ resolver: zodResolver(schema), mode: "onBlur" });

  const [consentGiven, setConsentGiven] = useState(false);
  const [consentTouched, setConsentTouched] = useState(false);
  // One combined optional opt-in (events/marketing + the RestoMag founding-member pitch in
  // the copy). Captured in the backend as marketing_opt_in; there is no separate signal today.
  const [keepInLoop, setKeepInLoop] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = useSubmitLead();
  const { code, fromUrl, setCode } = useEntryCode();

  const onVerify = useCallback((t: string) => setToken(t), []);
  const onTokenGone = useCallback(() => setToken(null), []);

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // Pull focus + scroll to the submit error so it can't be missed — the old inline line
  // sat above the Turnstile widget and was easy to overlook.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (formError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
      errorRef.current.focus();
    }
  }, [formError, reduceMotion]);

  const consentError = consentTouched && !consentGiven;
  const canSubmit = consentGiven && token !== null && code !== "" && !submit.isPending;

  async function onValid(values: FieldValues) {
    setFormError(null);
    setConsentTouched(true);
    if (!consentGiven) return;
    if (token === null) {
      setFormError(messageForCode("turnstile_failed"));
      return;
    }
    if (code === "") {
      setFormError(messageForCode("code_required"));
      return;
    }
    // Stamp the +1 country code onto North-American phone numbers (the input shows "+1"
    // as a fixed prefix) so leads store the full international number.
    const fieldValues: FieldValues = { ...values };
    for (const f of event.fieldSet) {
      const v = fieldValues[f.key];
      if (f.type === "tel" && typeof v === "string" && v.trim() !== "" && !v.trim().startsWith("+")) {
        fieldValues[f.key] = `+1 ${v.trim()}`;
      }
    }

    const payload: SubmitLeadPayload = {
      event_slug: event.slug,
      field_values: fieldValues,
      consent_given: consentGiven,
      marketing_opt_in: keepInLoop,
      turnstile_token: token,
      honeypot,
      source,
      code,
    };
    const result = await submit.mutateAsync(payload);
    if (result.ok) {
      setDone(true);
      onSuccess?.();
      // Bring the success message into view rather than leaving the user at the
      // bottom of the form they just scrolled through.
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setFormError(messageForCode(result.code));
    }
  }

  // Reset everything for the next person on a shared/kiosk device — no reload, no persist.
  const enterAnother = useCallback(() => {
    reset();
    setConsentGiven(false);
    setConsentTouched(false);
    setKeepInLoop(false);
    setToken(null);
    setHoneypot("");
    setFormError(null);
    setDone(false);
  }, [reset]);

  return (
    <div className="rounded-2xl border border-white/10 bg-black p-5 shadow-[0_30px_60px_-40px_rgba(0,0,0,.7)] md:p-6">
      {/* Card header */}
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-flame">
          <IconTrophy size={14} />
          Enter to win
        </span>
        <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-white/40">~30 sec</span>
      </div>

      {done ? (
        <div className="py-8 text-center">
          {reduceMotion ? (
            <StaticSuccessMark />
          ) : (
            <div className="mb-1">
              <Suspense fallback={<StaticSuccessMark />}>
                <SuccessAnimation />
              </Suspense>
            </div>
          )}
          <h2 className="text-xl font-bold tracking-tight text-white">You&apos;re in!</h2>
          <p className="mx-auto mt-1.5 max-w-[34ch] text-sm text-white/65">
            You&apos;re entered{winnerAnnounced ? `. The winner is announced ${winnerAnnounced}.` : "."}
          </p>
          <button
            type="button"
            onClick={enterAnother}
            className="mt-6 min-h-touch w-full rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-[15px] font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
          >
            Enter another →
          </button>
        </div>
      ) : (
        <>
          <h2 className="text-xl font-bold tracking-tight text-white">Get your name in the draw</h2>
          <p className="mb-4 mt-1 text-sm text-white/65">
            Free to enter{winnerAnnounced ? ` · winner announced ${winnerAnnounced}` : ""}.
          </p>

          <form onSubmit={handleSubmit(onValid)} className="space-y-3" noValidate>
            {/* Honeypot: off-screen, not display:none (some bots skip hidden). */}
            <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
              <label>
                Leave this field empty
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </label>
            </div>

            {event.fieldSet.map((field) => (
              <FormField
                key={field.key}
                field={field}
                register={register}
                error={errors[field.key]?.message as string | undefined}
              />
            ))}

            {/* Entry code — hidden when supplied via the QR/entry-card URL. */}
            {fromUrl ? null : (
              <label className="block">
                <span className={LABEL_CLS}>Entry code</span>
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  placeholder="From your entry card"
                  onChange={(e) => setCode(e.target.value)}
                  className={`${INPUT_CLS} px-4`}
                />
              </label>
            )}

            {/* Consent & acknowledgment */}
            <fieldset
              className={`rounded-xl border bg-white/[0.02] p-4 ${consentError ? "border-flame/60" : "border-white/10"}`}
            >
              <legend className="sr-only">Consent and acknowledgment</legend>
              <p className="mb-3 flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-flame">
                <IconShieldCheck size={14} />
                Consent &amp; acknowledgment
              </p>

              <div className="space-y-3">
                <ConsentCheckbox checked={consentGiven} onChange={setConsentGiven} invalid={consentError}>
                  {event.consent_copy} <span className="text-flame">*</span>
                </ConsentCheckbox>

                <div className="border-t border-white/[.08]" />

                {/*
                  GUARD — single-event assumption: this is the SHARED form for every event
                  (/:slug), but the "RestoMag founding-member early access" clause is
                  RestoMag-specific. Today the opt-in is captured only via marketing_opt_in
                  (there is no separate founding-member column on purpose — see git history
                  for the dropped restomag_signup migration + plumbing). One RestoMag-operated
                  event today, so it's fine. Before a SECOND / third-party event goes live,
                  gate this clause to RestoMag events (e.g. a slug allowlist) and fall back to
                  marketing-only copy for tenants — otherwise a third-party organizer's form
                  carries a RestoMag product pitch. If you also want founding-member interest
                  as a distinct signal then, re-add the restomag_signup column at the same time.
                */}
                <ConsentCheckbox checked={keepInLoop} onChange={setKeepInLoop}>
                  <span className="font-semibold text-white">Keep me in the loop</span>{" "}
                  <span className="text-white/40">(optional).</span> Email me about this event, future shows,
                  builds and offers — and sign me up for <span className="font-semibold text-white">RestoMag</span>{" "}
                  founding-member early access, with my first month free at launch. I can opt out anytime.
                </ConsentCheckbox>
              </div>

              {consentError && <p className="mt-3 text-xs text-flame">Required to enter the draw.</p>}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-white/[.08] pt-3">
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-white/40">
                  <IconLock size={12} />
                  Never sold ·{" "}
                  <a href={MAILTO_DELETE} className="underline underline-offset-2 hover:text-white">
                    Delete anytime
                  </a>
                </span>
                <span className="flex items-center gap-3 text-[11px] text-white/55">
                  <a
                    href="https://www.restomag.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-white"
                  >
                    Privacy notice
                  </a>
                  <a
                    href="https://www.restomag.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-white"
                  >
                    Terms
                  </a>
                </span>
              </div>
            </fieldset>

            <Turnstile onVerify={onVerify} onExpire={onTokenGone} onError={onTokenGone} />

            {formError && (
              <div
                ref={errorRef}
                role="alert"
                aria-live="assertive"
                tabIndex={-1}
                className="flex items-start gap-2.5 rounded-xl border border-flame/50 bg-flame/10 px-4 py-3 text-sm text-white outline-none"
              >
                <IconWarning size={18} className="mt-0.5 shrink-0 text-flame" />
                <span>{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              aria-busy={submit.isPending}
              className="min-h-touch w-full rounded-xl bg-flame px-6 py-3.5 text-[15px] font-bold text-white transition hover:bg-flame-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submit.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <span
                    className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-white/40 border-t-white"
                    role="status"
                    aria-label="Entering you in the draw…"
                  />
                  Entering…
                </span>
              ) : (
                "Enter to win →"
              )}
            </button>
          </form>
        </>
      )}

      {/* Card footer */}
      <div className="mt-5 flex items-center justify-between border-t border-white/[.08] pt-4">
        <span className="flex items-center gap-1.5 font-mono text-[11px] text-white/40">
          <IconLock size={14} />
          Secure
        </span>
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/35">
          powered by
          <img src="/restomag-wordmark.png" alt="RestoMag" className="h-3.5 w-auto" />
        </span>
      </div>
    </div>
  );
}

/* Static success checkmark — fallback while the Lottie loads and for reduced-motion users. */
function StaticSuccessMark() {
  return (
    <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-flame text-white">
      <IconCheck size={24} />
    </span>
  );
}

/* One field from the event's field set, styled for the dark card. */
function FormField({
  field,
  register,
  error,
}: {
  field: FieldDef;
  register: UseFormRegister<FieldValues>;
  error?: string;
}) {
  const id = `field-${field.key}`;
  const Leading = FIELD_ICONS[field.key];
  const isTel = field.type === "tel";
  const reg = register(field.key);
  const a11y = {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? `${id}-error` : undefined,
  };

  return (
    <label className="block">
      <span className={LABEL_CLS}>
        {field.label}
        {field.required && <span className="ml-0.5 text-flame">*</span>}
      </span>
      <span className="relative block">
        {field.type === "select" ? (
          <>
            <select {...a11y} {...reg} defaultValue="" className={`${INPUT_CLS} appearance-none px-4 pr-10`}>
              <option value="" disabled={field.required}>
                Select…
              </option>
              {field.options?.map((opt) => (
                <option key={opt} value={opt} className="bg-ink text-white">
                  {opt}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </>
        ) : field.type === "textarea" ? (
          <textarea {...a11y} {...reg} rows={3} className={`${INPUT_CLS} h-auto resize-y py-3 px-4`} />
        ) : (
          <>
            {isTel ? (
              // Fixed "+1" country-code prefix (Canada/US) — the input holds the national
              // number; the +1 is stamped on at submit. pointer-events-none so taps focus the input.
              <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
                <IconPhone size={18} className="text-white/40" />
                <span className="text-[15px] font-medium text-white/70">+1</span>
                <span className="h-5 w-px bg-white/15" />
              </span>
            ) : Leading ? (
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                <Leading size={18} />
              </span>
            ) : null}
            <input
              {...a11y}
              {...reg}
              type={field.type === "email" ? "email" : isTel ? "tel" : "text"}
              inputMode={isTel ? "tel" : field.type === "email" ? "email" : undefined}
              autoComplete={field.type === "email" ? "email" : isTel ? "tel" : undefined}
              maxLength={isTel ? 14 : undefined}
              placeholder={placeholderFor(field)}
              // Format phone keystrokes live, then hand the formatted value to RHF.
              onChange={
                isTel
                  ? (e) => {
                      e.target.value = formatPhoneInput(e.target.value);
                      return reg.onChange(e);
                    }
                  : reg.onChange
              }
              className={`${INPUT_CLS} ${isTel ? "pl-[4.5rem] pr-4" : Leading ? "pl-10 pr-4" : "px-4"}`}
            />
          </>
        )}
      </span>
      {error && (
        <span id={`${id}-error`} className="mt-1 block text-xs text-flame">
          {error}
        </span>
      )}
    </label>
  );
}

/**
 * Live North-American phone formatter — progressively formats keystrokes into
 * "(604) 555-0182" as the user types. Display-only: the value is still validated by the
 * shared Zod schema and normalized to digits server-side (extractContact). Caps at the
 * 10 NANP digits; non-digits are ignored so paste / backspace stay predictable.
 */
function formatPhoneInput(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function placeholderFor(field: FieldDef): string | undefined {
  switch (field.key) {
    case "full_name":
    case "name":
      return "First and last";
    case "email":
      return "you@email.com";
    case "phone":
    case "mobile":
      return "(604) 555-0182";
    case "vehicle":
      return "e.g. '69 Camaro, LS swap";
    default:
      return undefined;
  }
}

/* Styled checkbox + rich label; keeps a native input for a11y. */
function ConsentCheckbox({
  checked,
  onChange,
  invalid,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <span className="relative mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={invalid}
          className="peer absolute inset-0 cursor-pointer opacity-0"
        />
        <span
          className={`flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-flame/50 ${
            checked
              ? "border-flame bg-flame text-white"
              : invalid
                ? "border-flame/70 bg-white/[0.03]"
                : "border-white/20 bg-white/[0.03]"
          }`}
        >
          {checked && <IconCheck size={12} />}
        </span>
      </span>
      <span className="text-[13px] leading-snug text-white/70">{children}</span>
    </label>
  );
}
